import {
  FeedbackListQueryResult,
  useFeedbackListLazyQuery,
  useFeedbackListQuery,
} from '@graphql/queries/__generated__/feedback.generated'
import { Theme, useTheme } from '@mui/material'
import Rating from '@mui/material/Rating'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import PlaceHolder from 'images/placeholder-img.svg'
import _ from 'lodash'
import React, { useEffect, useMemo, useRef } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { dateToShortString } from '../../../misc/utils'
import { maybe } from '../../../utils/misc'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../../hooks/useIsVisible'
import { Link } from 'react-router-dom'
import { Ordering } from '../../../../generated/gqlTypes'
import { getProductUrl } from '../../../urls'

const MAX_SHOW_FEEDBACK = 18
const useStyles = makeStyles((theme: Theme) => ({
  slide: {
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      '&:nth-of-type(2n):after': {
        backgroundColor: 'transparent',
      },
    },
    [theme.breakpoints.up('md')]: {
      alignSelf: 'stretch',
      height: 150,
      '&:after': {
        content: '""',
        backgroundColor: theme.palette.divider,
        width: 1,
        position: 'absolute',
        right: 20,
        top: 0,
        bottom: 0,
      },
    },
    [theme.breakpoints.up('lg')]: {
      '&:nth-of-type(3n):after': {
        backgroundColor: 'transparent',
      },
    },
  },
}))

const FeedbackSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  const classes = useStyles()
  return (
    <Swiper {...swiperOptions}>
      {_.range(MAX_SHOW_FEEDBACK).map(id => (
        <SwiperSlide key={id} className={classes.slide}>
          <div className='h-full pr-0 md:pr-40 relative'>
            <div className='flex items-center relative'>
              <div className='relative w-1/3 mr-10' style={{ paddingBottom: '39%' }}>
                <div className='absolute inset-0 h-full'>
                  <LazyLoadImage
                    alt={'skeleton'}
                    effect='opacity'
                    placeholderSrc={PlaceHolder}
                    src={PlaceHolder}
                    wrapperClassName='h-full w-full'
                    className='w-full h-full object-contain object-center'
                  />
                </div>
              </div>
              <div className='flex flex-col justify-center flex-1'>
                <p>
                  <span className='author'>
                    <Skeleton width={100} />
                  </span>
                  <span>
                    <Skeleton width={60} />
                  </span>
                </p>
                <Skeleton width={100} />
                <p>
                  <Skeleton width={'70%'} />
                  <Skeleton width={'80%'} />
                  <Skeleton width={'60%'} />
                </p>
              </div>
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

interface FeedbackProps {}

const Feedback: React.FC<FeedbackProps> = ({}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })

  const [loadFeedbackList, feedbackQuery] = useFeedbackListLazyQuery({
    variables: {
      offset: 0,
      limit: MAX_SHOW_FEEDBACK,
      score: 5,
      sortBy: [{ timeCreated: Ordering.Desc }],
    },
  })

  useEffect(() => {
    if (isVisible && !feedbackQuery.data) {
      loadFeedbackList()
    }
  }, [isVisible, feedbackQuery.data])

  const feedback = useMemo<FeedbackListQueryResult['data']['feedbackList']['results']>(
    () => maybe(() => feedbackQuery.data.feedbackList.results, []),
    [feedbackQuery.data]
  )

  const swiperOptions: SwiperOptions = {
    spaceBetween: 0,
    slidesPerView: 3,
    lazy: true,
    preloadImages: false,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-feedback`,
      dynamicBullets: true,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: feedback.length > 2,
    allowSlidePrev: feedback.length > 2,
    navigation: {
      prevEl: `#swiper-button-prev-feedback`,
      nextEl: `#swiper-button-next-feedback`,
    },
    autoplay: {
      disableOnInteraction: true,
      delay: 5000,
      waitForTransition: true,
    },
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        autoHeight: true,
      },
      [theme.breakpoints.values.md]: {
        slidesPerView: 2,
        slidesPerGroup: 2,
      },
      [theme.breakpoints.values.lg]: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 1000,
      },
    },
  }
  return (
    <section ref={ref}>
      <div className='bg-orange py-40 px-10'>
        <h3 className='my-6 pb-30 text:2xl md:text-5xl font-bold uppercase md:px-40 lg:px-60 font-futura text-center'>
          {t('title.feedback', 'We love your feedback')}
        </h3>
        <div className='relative md:px-40 lg:px-60'>
          {!feedback.length && feedbackQuery.loading && <FeedbackSkeleton swiperOptions={swiperOptions} />}
          {!!feedback.length && (
            <>
              <Swiper {...swiperOptions}>
                {feedback.map(node => {
                  return (
                    <SwiperSlide key={node.id} className={classes.slide}>
                      <div className='h-full pr-0 md:pr-40 relative'>
                        <div className='flex items-center relative'>
                          {node.line?.product?.thumbnailUrl && (
                            <div className='relative w-1/3 mr-10' style={{ paddingBottom: '39%' }}>
                              <Link
                                to={
                                  node.siteSpecifics
                                    ? getProductUrl(node.siteSpecifics.slug, node.siteSpecifics.pk)
                                    : '#!'
                                }
                                target={node.siteSpecifics ? '_blank' : '_self'}
                              >
                                <div className='absolute inset-0 h-full'>
                                  <LazyLoadImage
                                    alt={node.title}
                                    effect='opacity'
                                    placeholderSrc={PlaceHolder}
                                    src={node.line?.product?.thumbnailUrl || PlaceHolder}
                                    wrapperClassName='h-full w-full'
                                    className='w-full h-full object-contain object-center'
                                  />
                                </div>
                              </Link>
                            </div>
                          )}
                          {!node.line?.product?.thumbnailUrl && (
                            <div className='relative' style={{ paddingBottom: '39%' }} />
                          )}
                          <div className='flex flex-col justify-center flex-1'>
                            <p>
                              <span className='author'>{node.userName}</span>
                              <span> {dateToShortString(node.timeCreated)}</span>
                            </p>
                            <Rating readOnly size='small' value={node.score} />
                            <p className='md:line-clamp-4'>{node.comment}</p>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
              <div
                id={`swiper-pagination-feedback`}
                className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
                  feedback.length > 1 ? 'block' : 'hidden'
                } md:hidden`}
              />
              <div
                id={`swiper-button-prev-feedback`}
                className='swiper-button-prev swiper-button--small hidden md:block'
              />
              <div
                id={`swiper-button-next-feedback`}
                className='swiper-button-next swiper-button--small hidden md:block'
              />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default Feedback
